import { Component, attr } from '../Component'
import { element } from '../CustomElement'
import { i18n } from '../../i18n/I18n'

import { inject } from '../../services/di'
import { LoyaltyCardValidation } from './LoyaltyCardValidation'

import { AnalyticsService } from '../../services/AnalyticsService'
import { AuthenticationService } from '../../services/AuthenticationService'
import { CouponService } from '../../services/CouponService'
import { LoyaltyProgramService } from '../../services/LoyaltyProgramService'
import { EventService } from '../../services/EventService'
import { AccountsService } from '../../services/AccountsService'
import {
    LoyaltyProgram,
    LoyaltyCard,
    ToastLevel,
} from '../../definitions/CommonDefinitions'
import { LoyaltyCardSource } from '../../services/definitions/IAnalyticsService'
import { ToastService } from '../../services/ToastService'
import { isValidProgramInput } from '../../utils/loyaltyprograms'

interface LoyaltyCardInputProps extends JSX.HTMLProps {
    programId: number
    editing?: boolean
    cancelable?: boolean
    context: LoyaltyCardSource
}

@element('flipp-loyalty-card-input')
export class LoyaltyCardInputElement extends Component<
    LoyaltyCardInputProps,
    LoyaltyProgram
> {
    @inject auth: AuthenticationService
    @inject loyaltyService: LoyaltyProgramService
    @inject accountsService: AccountsService
    @inject eventService: EventService
    @inject couponService: CouponService
    @inject analytics: AnalyticsService
    @inject toast: ToastService

    @attr programId: number
    @attr cancelable: boolean
    @attr context: number

    refs: {
        cardInput: HTMLInputElement
        pinInput: HTMLInputElement
        validationMsg: LoyaltyCardValidation
    }

    state = {
        adding: false,
        input: ['', ''],
        cardOrPhoneValue: '',
    }

    prepare() {
        return this.loyaltyService.getLoyaltyProgram(this.programId)
    }

    render(program: LoyaltyProgram) {
        if (!program) {
            return null
        }

        const disabledAttr: { [key: string]: boolean } = {}
        if (this.state.adding) {
            disabledAttr['disabled'] = true
        }

        const cardRadioAttrs: { [key: string]: boolean } = {}
        const phoneRadioAttrs: { [key: string]: boolean } = {}
        if (
            !this.state.cardOrPhoneValue ||
            this.state.cardOrPhoneValue === 'card'
        ) {
            cardRadioAttrs['checked'] = true
        }
        if (this.state.cardOrPhoneValue === 'phone') {
            phoneRadioAttrs['checked'] = true
        }

        return (
            <form
                onSubmit={this.handleAddCardSubmit_}
                onReset={this.handleAddCardReset_}
                classList={{ active: this.state.adding }}
            >
                <div class="input-container" flex-vertical flex-grow>
                    {program.validationMethod === 'cardorphone' ? (
                        <div class="cardorphone" flex-horizontal>
                            <label onClick={this.handleCardOrPhoneRadio_}>
                                <input
                                    type="radio"
                                    name="cardorphone"
                                    value="card"
                                    {...cardRadioAttrs}
                                />
                                {i18n('card_number')}
                            </label>
                            <label onClick={this.handleCardOrPhoneRadio_}>
                                <input
                                    type="radio"
                                    name="cardorphone"
                                    value="phone"
                                    {...phoneRadioAttrs}
                                />
                                {i18n('phone_number')}
                            </label>
                        </div>
                    ) : null}

                    <div class="card-entry" flex-horizontal flex-grow>
                        <div class="form-elements" flex-horizontal flex-grow>
                            <input
                                type="text"
                                name="cardnumber"
                                ref="cardInput"
                                value={this.state.input[0]}
                                placeholder={
                                    program.validationMethod === 'cardonly'
                                        ? i18n('enter_card_number')
                                        : program.validationMethod ===
                                                'phoneonly' ||
                                            program.validationMethod ===
                                                'phoneandpin'
                                          ? i18n('enter_phone_number')
                                          : program.validationMethod ===
                                                  'cardorphone' &&
                                              (!this.state.cardOrPhoneValue ||
                                                  this.state
                                                      .cardOrPhoneValue ===
                                                      'card')
                                            ? i18n('enter_card_number')
                                            : program.validationMethod ===
                                                    'cardorphone' &&
                                                this.state.cardOrPhoneValue ===
                                                    'phone'
                                              ? i18n('enter_phone_number')
                                              : null
                                }
                                flex-grow
                                {...disabledAttr}
                            />
                            {program.validationMethod === 'phoneandpin' ? (
                                <input
                                    type="text"
                                    name="pinnumber"
                                    ref="pinInput"
                                    value={this.state.input[1]}
                                    placeholder={i18n('enter_pin')}
                                />
                            ) : null}
                            <input
                                type="submit"
                                name="submit"
                                value="+"
                                {...disabledAttr}
                            />
                            <div class="loading">
                                <div class="spinner"></div>
                            </div>
                        </div>
                        {this.cancelable ? (
                            <input
                                type="reset"
                                name="cancel"
                                value={i18n('cancel')}
                                {...disabledAttr}
                            />
                        ) : null}
                    </div>
                    <LoyaltyCardValidation
                        ref="validationMsg"
                        validationState="ok"
                    />
                </div>
            </form>
        )
    }

    private handleCardOrPhoneRadio_ = (e: Event) => {
        const radio = e.target as HTMLInputElement
        if (radio.value) {
            this.state.cardOrPhoneValue = radio.value
            this.invalidate()
        }
    }

    private handleAddCardSubmit_ = (e: Event) => {
        e.preventDefault()
        e.stopPropagation()

        const cardValue = this.refs.cardInput.value
        const pinValue = this.refs.pinInput ? this.refs.pinInput.value : null
        const program = this.model
        const isValid = isValidProgramInput(program, [cardValue, pinValue])

        if (!isValid[0]) {
            if (program.validationMethod === 'cardorphone') {
                this.refs.validationMsg.validationState =
                    this.state.cardOrPhoneValue === 'card'
                        ? 'cardError'
                        : 'phoneError'
            } else {
                this.refs.validationMsg.validationState =
                    program.validationMethod === 'cardonly'
                        ? 'cardError'
                        : 'phoneError'
            }
            return
        } else if (!isValid[1]) {
            this.refs.validationMsg.validationState = 'pinError'
            return
        } else {
            this.refs.validationMsg.validationState = 'ok'
        }

        this.state.adding = true
        this.state.input = [cardValue, pinValue]
        this.invalidate().then(() => {
            const card: LoyaltyCard = {
                loyaltyProgramId: program.id,
                name: program.name,
            }
            if (program.validationMethod === 'cardonly') {
                card.cardId = cardValue
            } else if (program.validationMethod === 'phoneandpin') {
                card.phoneNumber = cardValue
                card.pinNumber = pinValue
            } else if (program.validationMethod === 'phoneonly') {
                card.phoneNumber = cardValue
            } else if (program.validationMethod === 'cardorphone') {
                if (this.state.cardOrPhoneValue === 'card') {
                    card.cardId = cardValue
                } else if (this.state.cardOrPhoneValue === 'phone') {
                    card.phoneNumber = cardValue
                }
            }
            this.accountsService
                .postLoyaltyCards(this.auth.user.token, card)
                .then(
                    () => {
                        this.loyaltyService.clearLoyaltyCards()
                        this.couponService.clearClippedCoupons()
                        this.eventService.fireLoyaltyCardEvent(
                            'add',
                            program.id,
                            this,
                            true
                        )
                        this.toast.fireToastEvent(
                            i18n('add_card_success', { name: card.name }),
                            ToastLevel.OK,
                            false,
                            this
                        )
                        this.analytics.trackLoyaltyCardAdded(
                            program,
                            this.context
                        )
                    },
                    (err: Error) => {
                        this.state.adding = false
                        this.toast.fireToastEvent(
                            `${i18n('add_card_error')} ${err.message}`,
                            ToastLevel.ERROR,
                            false,
                            this
                        )
                        this.invalidate()
                    }
                )
        })
    }

    private handleAddCardReset_ = () => {
        this.dispatchEvent(new Event('reset', { bubbles: true }))
    }
}
